/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { IconButton, Menu, Box, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyIcon from "@mui/icons-material/Key";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  StyledHeaderCellTypo,
  StyledBodyCellTypo,
  StyledMenuItem,
  StyledTypography,
} from "../Users.jsx";
import { useState } from "react";
import PropTypes from "prop-types";
import MFAModal from "../modals/MFAModal";
import DeactivateModal from "../modals/DeactivateModal.jsx";
import DeleteUserModal from "../modals/DeleteUserModal";
import UserDetailsModal from "../modals/UserDetailsModal";
import { toggleMFAAuth } from "../../../../Services/redux/global-admin-users/GlobalAdminUsersActions.js";
import { useDispatch } from "react-redux";

const ActiveTableData = ({
  activeData,
  searchTerm,
  currentTabStatus,
  loading,
}) => {
  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState(false);
  const [openMFAModal, setOpenMFAModal] = useState(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);

  const [userDetails, setUserDetails] = useState(null);
  const [mfaEnabled, setMfaEnabled] = useState(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Sorting state
  const [sortBy, setSortBy] = useState("userName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hoveredColumn, setHoveredColumn] = useState(null);

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Render sortable header with icon
  const renderSortableHeader = (fieldName, displayName) => {
    const renderSortIcon = () => {
      if (sortBy === fieldName) {
        if (sortOrder === "asc") {
          return (
            <ArrowUpwardIcon
              sx={{
                color: "#12141599",
                height: "16px",
                width: "16px",
                transition: "color 0.2s ease",
              }}
            />
          );
        } else {
          return (
            <ArrowDownwardIcon
              sx={{
                color: "#12141599",
                height: "16px",
                width: "16px",
                transition: "color 0.2s ease",
              }}
            />
          );
        }
      } else if (hoveredColumn === fieldName) {
        return (
          <ArrowUpwardIcon
            sx={{
              color: "#12141599",
              height: "16px",
              width: "16px",
              opacity: 0.5,
              transition: "opacity 0.2s ease",
            }}
          />
        );
      }
      return null;
    };

    return (
      <Box
        sx={{ textAlign: "center", display: "flex", alignItems: "center" }}
        onMouseEnter={() => setHoveredColumn(fieldName)}
        onMouseLeave={() => setHoveredColumn(null)}
      >
        <StyledHeaderCellTypo>{displayName}</StyledHeaderCellTypo>
        <IconButton
          sx={{
            cursor: "pointer",
            borderRadius: "4px",
            marginLeft: "8px",
            width: "24px",
            height: "24px",
            padding: "4px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          onClick={() => handleSort(fieldName)}
        >
          {renderSortIcon()}
        </IconButton>
      </Box>
    );
  };

  // Helper function to normalize values for sorting
  const normalizeValue = (value) => {
    if (value == null) return "";
    if (typeof value === "string") return value.toLowerCase();
    return value;
  };

  // Helper function to compare two values
  const compareValues = (aValue, bValue) => {
    if (aValue > bValue) return 1;
    if (aValue < bValue) return -1;
    return 0;
  };

  // Sort the data
  const sortedData = [...(activeData || [])].sort((a, b) => {
    const aValue = normalizeValue(a[sortBy]);
    const bValue = normalizeValue(b[sortBy]);
    const comparison = compareValues(aValue, bValue);

    return sortOrder === "asc" ? comparison : -comparison;
  });

  const createRefetchPayload = () => {
    const refetchPayload = {
      status: [currentTabStatus],
    };
    if (searchTerm && searchTerm.trim() !== "") {
      refetchPayload.email = [searchTerm.trim()];
    }
    return refetchPayload;
  };

  const handleDeleteUserOpen = () => {
    setOpenDeleteUserModal(true);
  };

  const handleDeactivateClose = () => {
    setOpenDeactivateModal(false);
  };

  const handleDeleteUserClose = () => {
    setOpenDeleteUserModal(false);
  };

  const handleUserDetailsOpen = (id) => {
    setSelectedRowId(id);
    const details = activeData.find((user) => user.id === id);
    setUserDetails(details);
    setOpenUserDetailsModal(true);
  };

  const handleUserDetailsClose = (ind) => {
    setOpenUserDetailsModal(false);
  };

  const handleVertIconClick = (event, rowIndexId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(rowIndexId);
    const userData = activeData.find((user) => user.id === rowIndexId);
    setMfaEnabled(userData?.customMFAEnabled);
  };

  const handleVertIconModalClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleDeactivateOpen = () => {
    setOpenDeactivateModal(true);
  };

  const handleMFAOpen = () => {
    const userData = activeData.find((user) => user.id === selectedRowId);
    setUserDetails(userData);
    setOpenMFAModal(true);
  };

  const handleMFAClose = () => {
    try {
      const data = activeData.find((user) => user.id === selectedRowId);
      const refetchPayload = createRefetchPayload();
      dispatch(
        toggleMFAAuth(
          { ...data, customMFAEnabled: !mfaEnabled },
          refetchPayload
        )
      );
    } catch (err) {
      console.log(err);
    } finally {
      setOpenMFAModal(false);
      setAnchorEl(false);
    }
  };

  const handleMFACancel = () => {
    setOpenMFAModal(false);
    setAnchorEl(null);
  };

  // DataGrid column definitions
  const columns = [
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderHeader: () => renderSortableHeader("email", "Email"),
      renderCell: (params) => {
        const email = params.row.email || "...";
        const displayEmail =
          email.length > 30 ? email.substring(0, 30) + "..." : email;
        const showTooltip = email !== "...";

        return (
          <Box
            onClick={() => handleUserDetailsOpen(params.row.id)}
            sx={{
              cursor: "pointer",
              width: "100%",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              padding: "8px",
            }}
          >
            <Tooltip
              title={showTooltip ? email : ""}
              arrow
              placement="top"
              disableHoverListener={!showTooltip}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#121415F2",
                    padding: "8px 12px 8px 12px",
                    "& .MuiTooltip-arrow": {
                      color: "#121415F2",
                    },
                  },
                },
              }}
            >
              <StyledBodyCellTypo
                sx={{
                  lineHeight: "24px",
                  letterSpacing: "0.1px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayEmail}
              </StyledBodyCellTypo>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "userName",
      headerName: "Username",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderSortableHeader("userName", "Username"),
      renderCell: (params) => {
        const userName = params.row.userName || "...";
        const displayUserName =
          userName.length > 20 ? userName.substring(0, 20) + "..." : userName;
        const showTooltip = userName !== "...";

        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              padding: "8px",
            }}
          >
            <Tooltip
              title={showTooltip ? userName : ""}
              arrow
              placement="top"
              disableHoverListener={!showTooltip}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#121415F2",
                    padding: "8px 12px 8px 12px",
                    "& .MuiTooltip-arrow": {
                      color: "#121415F2",
                    },
                  },
                },
              }}
            >
              <StyledBodyCellTypo
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayUserName}
              </StyledBodyCellTypo>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderHeader: () => renderSortableHeader("role", "Role"),
      renderCell: (params) => {
        const role = params.row.role || "...";
        const displayRole =
          role.length > 15 ? role.substring(0, 15) + "..." : role;
        const showTooltip = role !== "...";

        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              padding: "8px",
            }}
          >
            <Tooltip
              title={showTooltip ? role : ""}
              arrow
              placement="top"
              disableHoverListener={!showTooltip}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#121415F2",
                    padding: "8px 12px 8px 12px",
                    "& .MuiTooltip-arrow": {
                      color: "#121415F2",
                    },
                  },
                },
              }}
            >
              <StyledBodyCellTypo>{displayRole}</StyledBodyCellTypo>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Company Name",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderHeader: () => renderSortableHeader("companyName", "Company Name"),
      renderCell: (params) => {
        const companyName = params.row.companyName || "...";
        const displayCompanyName =
          companyName.length > 15
            ? companyName.substring(0, 15) + "..."
            : companyName;
        const showTooltip = companyName !== "...";

        return (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              padding: "8px",
            }}
          >
            <Tooltip
              title={showTooltip ? companyName : ""}
              arrow
              placement="top"
              disableHoverListener={!showTooltip}
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#121415F2",
                    padding: "8px 12px 8px 12px",
                    "& .MuiTooltip-arrow": {
                      color: "#121415F2",
                    },
                  },
                },
              }}
            >
              <StyledBodyCellTypo>{displayCompanyName}</StyledBodyCellTypo>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "customMFAEnabled",
      headerName: "MFA",
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center" }}>
          <StyledHeaderCellTypo>MFA</StyledHeaderCellTypo>
        </Box>
      ),
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            padding: "8px",
          }}
        >
          <Tooltip
            title={params.row.customMFAEnabled ? "Enabled" : "Disabled"}
            arrow
            placement="top"
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: "#121415F2",
                  padding: "8px 12px 8px 12px",
                  "& .MuiTooltip-arrow": {
                    color: "#121415F2",
                  },
                },
              },
            }}
          >
            <StyledBodyCellTypo>
              {params.row.customMFAEnabled ? "Enabled" : "Disabled"}
            </StyledBodyCellTypo>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleVertIconClick(e, params.row.id)}
          disabled={readOnly_GlobalAdmin}
        >
          <MoreVertIcon
            sx={{
              height: "16px",
              width: "16px",
              color: "#12141599",
            }}
          />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={sortedData || []}
          columns={columns}
          getRowId={(row) => row.id}
          pagination
          pageSizeOptions={[5, 10, 15]}
          paginationMode="client"
          disableColumnMenu
          disableRowSelectionOnClick
          loading={false}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "Items per page:",
            },
          }}
          sx={{
            "& .MuiDataGrid-columnHeader": {
              fontSize: "12px",
              fontWeight: "600",
              backgroundColor: "#E8E8E8 !important",
              color: "#121415",
              lineHeight: "30px",
              padding: "0px 10px",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid rgba(18, 20, 21, 0.2)",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "12px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              minHeight: "30px",
              lineHeight: "30px",
              padding: "16px",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid rgba(18, 20, 21, 0.2)",
              borderBottom: "none !important",
              fontSize: "12px",
              lineHeight: "20px",
              padding: "0px",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell:last-child": { borderRight: "none" },
            "& .MuiDataGrid-container--top [role=row]": {
              background: "#E8E8E8 !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              marginTop: "0px !important",
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              overflowX: "hidden !important",
            },
            "& .MuiDataGrid-row": {
              height: "56px",
              borderBottom: "1px solid rgba(18, 20, 21, 0.2)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            },
          }}
        />

        <Menu
          sx={{
            minWidth: "164px",
            minHeight: "118px",
            borderRadius: "4px",
            padding: "4px",
            display: "flex",
            gap: "2px",
          }}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleVertIconModalClose}
        >
          <StyledMenuItem
            sx={{
              color: "#2A2C2D",
            }}
            onClick={handleMFAOpen}
          >
            <KeyIcon
              sx={{
                color: "#12141599",
                height: "16px",
                width: "16px",
              }}
            />
            <StyledTypography>
              {mfaEnabled ? "Disable" : "Enable"} MFA
            </StyledTypography>
          </StyledMenuItem>

          <StyledMenuItem
            sx={{
              color: "#2A2C2D",
            }}
            onClick={handleDeactivateOpen}
          >
            <PersonOffIcon
              sx={{
                height: "16px",
                width: "16px",
              }}
            />
            <StyledTypography>Deactivate User</StyledTypography>
          </StyledMenuItem>
          <hr />
          <StyledMenuItem
            sx={{
              color: "#B50B00",
            }}
            onClick={handleDeleteUserOpen}
          >
            <DeleteIcon
              sx={{
                height: "16px",
                width: "16px",
              }}
            />
            <StyledTypography>Delete</StyledTypography>
          </StyledMenuItem>
        </Menu>
      </Box>

      <MFAModal
        status={mfaEnabled}
        openMFAModal={openMFAModal}
        handleMFAClose={handleMFAClose}
        handleMFACancel={handleMFACancel}
      />
      <DeactivateModal
        selectedRowIdId={selectedRowId}
        handleVertIconModalClose={handleVertIconModalClose}
        openDeactivateModal={openDeactivateModal}
        handleDeactivateClose={handleDeactivateClose}
        searchTerm={searchTerm}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
      />
      <DeleteUserModal
        openDeleteUserModal={openDeleteUserModal}
        handleDeleteUserClose={handleDeleteUserClose}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        currentTabStatus={currentTabStatus}
        selectedRowId={selectedRowId}
        searchTerm={searchTerm}
      />

      <UserDetailsModal
        openUserDetailsModal={openUserDetailsModal}
        handleUserDetailsClose={handleUserDetailsClose}
        userDetails={userDetails}
      />
    </>
  );
};

ActiveTableData.propTypes = {
  activeData: PropTypes.arrayOf(PropTypes.object),
  searchTerm: PropTypes.string,
  currentTabStatus: PropTypes.string,
  loading: PropTypes.bool,
};

export default ActiveTableData;
